import { byteBufferAsUint8Array, makeByteBuffer } from "../utils/utils.js";
/**
 * The entry stream MUST be named "__substg1.0_00030102" and consist of 8-byte entries, one for each
 * named property being stored. The properties are assigned unique numeric IDs (distinct from any property
 * ID assignment) starting from a base of 0x8000. The IDs MUST be numbered consecutively, like an array.
 * In this stream, there MUST be exactly one entry for each named property of the Message object or any of
 * its subobjects. The index of the entry for a particular ID is calculated by subtracting 0x8000 from it.
 * For example, if the ID is 0x8005, the index for the corresponding 8-byte entry would be 0x8005 – 0x8000 = 5.
 * The index can then be multiplied by 8 to get the actual byte offset into the stream from where to start
 * reading the corresponding entry.
 *
 * see: https://msdn.microsoft.com/en-us/library/ee159689(v=exchg.80).aspx
 */
export class EntryStream extends Array {
    /**
     * creates this object and reads all the EntryStreamItems from
     * the given storage
     */
    constructor(storage) {
        super();
        if (storage == null)
            return;
        const stream = storage.getStream("__substg1.0_00030102" /* PropertyTagLiterals.EntryStream */);
        if (stream.byteLength <= 0) {
            storage.addStream("__substg1.0_00030102" /* PropertyTagLiterals.EntryStream */, Uint8Array.of());
        }
        const buf = makeByteBuffer(undefined, stream);
        while (buf.offset < buf.limit) {
            const entryStreamItem = EntryStreamItem.fromBuffer(buf);
            this.push(entryStreamItem);
        }
    }
    /**
     * writes all the EntryStreamItems as a stream to the storage
     */
    write(storage, streamName = "__substg1.0_00030102" /* PropertyTagLiterals.EntryStream */) {
        const buf = makeByteBuffer();
        this.forEach(entry => entry.write(buf));
        storage.addStream(streamName, byteBufferAsUint8Array(buf));
    }
}
/**
 * Represents one item in the EntryStream
 */
export class EntryStreamItem {
    /**
     * creates this objcet and reads all the properties from the given buffer
     * @param buf {ByteBuffer}
     */
    static fromBuffer(buf) {
        const nameIdentifierOrStringOffset = buf.readUint16();
        const indexAndKindInformation = IndexAndKindInformation.fromBuffer(buf);
        return new EntryStreamItem(nameIdentifierOrStringOffset, indexAndKindInformation);
    }
    /**
     * creates this object from the properties
     * @param nameIdentifierOrStringOffset {number}
     * @param indexAndKindInformation {IndexAndKindInformation}
     */
    constructor(nameIdentifierOrStringOffset, indexAndKindInformation) {
        this.nameIdentifierOrStringOffset = nameIdentifierOrStringOffset;
        this.nameIdentifierOrStringOffsetHex = nameIdentifierOrStringOffset.toString(16).toUpperCase().padStart(4, "0");
        this.indexAndKindInformation = indexAndKindInformation;
    }
    /**
     * write all the internal properties to the given buffer
     * @param buf {ByteBuffer}
     */
    write(buf) {
        buf.writeUint32(this.nameIdentifierOrStringOffset);
        const packed = (this.indexAndKindInformation.guidIndex << 1) | this.indexAndKindInformation.propertyKind;
        buf.writeUint16(packed);
        buf.writeUint16(this.indexAndKindInformation.propertyIndex); //Doesn't seem to be the case in the spec.
        // Fortunately section 3.2 clears this up.
    }
}
export class IndexAndKindInformation {
    static fromBuffer(buf) {
        const propertyIndex = buf.readUint16();
        const packedValue = buf.readUint16();
        const guidIndex = (packedValue >>> 1) & 0xffff;
        const propertyKind = packedValue & 0x07;
        if (![0xff, 0x01, 0x00].includes(propertyKind))
            throw new Error("invalid propertyKind:" + propertyKind);
        return new IndexAndKindInformation(propertyIndex, guidIndex, propertyKind);
    }
    constructor(propertyIndex, guidIndex, propertyKind) {
        this.guidIndex = guidIndex;
        this.propertyIndex = propertyIndex;
        this.propertyKind = propertyKind;
    }
    write(buf) {
        buf.writeUint16(this.propertyIndex);
        buf.writeUint32(this.guidIndex + this.propertyKind);
    }
}
